import {WALLET} from "@irati-web/app/src/routeConfig"
import authentication from "@irati/auth"
import Flex from "@irati/ui/src/Flex"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {RouterLink} from "@irati/ui/src/Link"
import Slider from "@irati/ui/src/Slider"
import {H3} from "@irati/ui/src/Typography"
import {DESKTOP, LARGE_DESKTOP, PHONE, PHONE_LANDSCAPE, TABLET} from "@irati/ui/src/global/media"
import FormattedMessage from "@irati/utils/lang"
import Spacing from "@irati/ui/src/Spacing"
import PropTypes from "prop-types"
import React, {useState} from "react"
import styled, {css} from "styled-components"
import {WalletNames, Context} from "./shared"
import WalletCard from "./WalletCard"
import {PointsBlendModal} from "@irati-web/platform/src/components/PointsBlendModal/PointsBlendModal"
import {IconButton} from "@irati/ui/src/Button"

const Padding = styled.div<{last?: boolean}>`
  ${({theme, last}) =>
    !last &&
    css`
      padding-right: ${theme.spacing.two};
    `}
`

const SliderOverrides = styled.div<{count: number}>`
  .slick-slider.homepage-wallet-slider > span {
    /* for reasons unknown, the naviagation buttons (which this selector targets) are inline-block and cause things to shift down as you scroll */
    display: block;
  }

  /* When adjusting screen size, refresh between resizes as the Slider component performs differently when calcuating from fresh */
  ${({count}) =>
    count < 3 &&
    css`
      .slick-slider.homepage-wallet-slider .slick-track {
        /* here we are forcing the child 'slides' to follow flex grow 1 behaviour, and fill all the space the can */
        width: auto !important;
        display: flex;
      }

      .slick-slider.homepage-wallet-slider .slick-slide {
        flex: 1;
      }
    `}
`

/** slider options dict where key is the number of wallet cards */
const responsive: Record<number, any> = {
  1: [{breakpoint: PHONE, settings: {slidesToShow: 1}}],
  2: [
    {breakpoint: PHONE, settings: {slidesToShow: 1.1, slidesToScroll: 0.5}},
    {breakpoint: PHONE_LANDSCAPE, settings: {slidesToShow: 1.3, slidesToScroll: 0.5}},
    {breakpoint: TABLET, settings: {slidesToShow: 2}}
  ],
  3: [
    {breakpoint: PHONE_LANDSCAPE, settings: {slidesToShow: 1.3, slidesToScroll: 1}},
    {breakpoint: TABLET, settings: {slidesToShow: 2.2, slidesToScroll: 1}},
    {breakpoint: DESKTOP, settings: {slidesToShow: 2.4, slidesToScroll: 1}},
    {breakpoint: LARGE_DESKTOP, settings: {slidesToShow: 3}}
  ]
}

Wallet.contextTypes = {
  apps: PropTypes.object
}

export default function Wallet(_: any, {apps}: Context) {
  const tiles = []
  const [modalShown, setModalShown] = useState(false)

  if (authentication.apps("wallet.flexiRewards.canRewardEmployees")) {
    tiles.push(WalletNames.GIVE)
  }

  if (authentication.apps("wallet.flexiRewards")) {
    tiles.push(WalletNames.REWARDS)
  }

  if (authentication.apps("wallet.treatTokens")) {
    tiles.push(WalletNames.FLEXI)
  }

  const amount = tiles.length

  if (amount === 0) return null

  return (
    <Spacing marginTop={5}>
      <Flex alignItems="center" justifyContent="space-between" marginBottom={3}>
        <Flex alignItems="center" gap={1}>
          <H3>
            <FormattedMessage id="platform.homepage.lanes.wallet.nav.title" />
          </H3>
          <div style={{height: "16px" /* go away bootstrap */}}>
            <IconButton onClick={() => setModalShown((s) => !s)} icon="circleQuestion" inline />
          </div>
        </Flex>

        <RouterLink cta to={"/"} app={apps[WALLET]}>
          <Flex alignItems="center">
            <FormattedMessage id="platform.homepage.lanes.wallet.nav.linkText" />
            <Icon icon={IconTypes.arrowRight} marginLeft />
          </Flex>
        </RouterLink>
      </Flex>

      <SliderOverrides count={amount}>
        <Slider
          maintainSameHeight
          className="homepage-wallet-slider"
          settings={{
            responsive: responsive[amount]
          }}>
          {tiles?.map((wallet, i, arr) => (
            <Padding key={wallet} last={i === arr.length - 1}>
              <WalletCard apps={apps} name={wallet} />
            </Padding>
          ))}
        </Slider>
      </SliderOverrides>
      <PointsBlendModal shown={modalShown} onClose={() => setModalShown(false)} />
    </Spacing>
  )
}
